/** VUEX module for contract management */
import Vue from 'vue';

export default {
  namespaced: true,
  state: {
    // initial state
    interval: null,
    entityIdCurrent: null,
    timeInSeconds: 0,
    entities: {},
    list: [],
    fetchListError: null,
    isFetchingList: false,
    fetchSingleError: null,
    isFetchingSingle: false,
    createError: null,
    isCreating: false,
    updateError: null,
    isUpdating: false,
    deleteError: null,
    isDeleting: false,
    isActivo: true,
  },
  getters: {
    // getters
    activityIdCurrent: state => {
      return state.entityIdCurrent;
    },
    timeInSeconds: state => {
      return state.timeInSeconds;
    },
    /**
     * Return array of resources.
     */
    list: state => {
      return state.list.map(id => state.entities[id.toString()]);
    },
    /**
     * Get resource by id.
     */
    byId: state => {
      return id => state.entities[id.toString()];
    },
    /**
     * Return true if there is a logged error.
     */
    isError: state => {
      return (
        state.fetchListError !== null ||
        state.fetchSingleError !== null ||
        state.createError !== null ||
        state.updateError !== null ||
        state.deleteError !== null
      );
    },
    /**
     * Return true if there is a ongoing request.
     */
    isLoading(state) {
      return state.isFetchingList || state.isFetchingSingle || state.isCreating || state.isUpdating || state.isDeleting;
    },
  },
  mutations: {
    // mutations
    ACTIVE_ACTIVITY_TIMER: state => {
      state.isActivo = true;
    },
    INACTIVE_ACTIVITY_TIMER: state => {
      state.isActivo = false;
    },
    START_COUNT_TIME_IN_SECOND: state => {
      state.interval = setInterval(function () {
        if (state.isActivo) {
          state.timeInSeconds++;
        }
      }, 1000);
    },
    STOP_TIMER_IN_SECONDS: state => {
      clearInterval(state.interval);
      state.timeInSeconds = 0;
    },
    FETCH_LIST_START: state => {
      state.isFetchingList = true;
    },
    FETCH_LIST_SUCCESS: (state, response) => {
      const { data } = response;

      data.items.forEach(m => {
        Vue.set(state.entities, m.id.toString(), m);
      });

      state.list = data.items.map(m => m.id.toString());
      state.isFetchingList = false;
      state.fetchListError = null;
    },
    FETCH_LIST_ERROR: (state, error) => {
      state.list = [];
      state.fetchListError = error;
      state.isFetchingList = false;
    },
    FETCH_SINGLE_START: state => {
      state.isFetchingSingle = true;
    },
    FETCH_SINGLE_SUCCESS: (state, response) => {
      const { data } = response;
      const id = data.id.toString();

      Vue.set(state.entities, id, data);
      state.isFetchingSingle = false;
      state.fetchSingleError = null;
    },
    FETCH_SINGLE_ERROR: (state, error) => {
      state.fetchSingleError = error;
      state.isFetchingSingle = false;
    },
    FETCH_CREATE_START: state => {
      state.isCreating = true;
    },
    FETCH_CREATE_SUCCESS: (state, response) => {
      const { data } = response;
      if (data) {
        const id = data.id.toString();
        state.list.push(id.toString());
        state.entityIdCurrent = data.id;
        Vue.set(state.entities, id, data);
      }
      state.isCreating = false;
      state.createError = null;
    },
    FETCH_CREATE_ERROR: (state, error) => {
      state.createError = error;
      state.isCreating = false;
    },
    FETCH_UPDATE_START: state => {
      state.isUpdating = true;
    },
    FETCH_UPDATE_SUCCESS: (state, response) => {
      const { data } = response;
      const id = data.id.toString();

      Vue.set(state.entities, id, data);

      const listIndex = state.list.indexOf(id);

      if (listIndex >= 0) {
        Vue.set(state.list, listIndex, id);
      }

      state.isUpdating = false;
      state.updateError = null;
    },
    FETCH_UPDATE_ERROR: (state, error) => {
      state.updateError = error;
      state.isUpdating = false;
    },
    FETCH_DELETE_START: state => {
      state.isDeleting = true;
    },
    FETCH_DELETE_SUCCESS: (state, id) => {
      const listIndex = state.list.indexOf(id.toString());

      if (listIndex >= 0) {
        Vue.delete(state.list, listIndex);
      }

      Vue.delete(state.entities, id.toString());
      state.isDeleting = false;
      state.deleteError = null;
    },
    FETCH_DELETE_ERROR: (state, error) => {
      state.deleteError = error;
      state.isDeleting = false;
    },
  },
  actions: {
    // actions
    FETCH_LIST: ({ commit }) => {
      commit('FETCH_LIST_START');
      return new Promise((resolve, reject) => {
        Vue.prototype
          .$httpActivity({ url: 'subject-activities', method: 'GET' })
          .then(response => {
            resolve(response);
            commit('FETCH_LIST_SUCCESS', response);
          })
          .catch(err => {
            reject(err);
            commit('FETCH_LIST_ERROR', err);
          });
      });
    },
    FETCH_SINGLE: ({ commit }, id) => {
      commit('FETCH_SINGLE_START');
      return new Promise((resolve, reject) => {
        Vue.prototype
          .$httpActivity({ url: `subject-activities/${id}`, method: 'GET' })
          .then(response => {
            resolve(response);
            commit('FETCH_SINGLE_SUCCESS', response);
          })
          .catch(err => {
            reject(err.response);
            commit('FETCH_SINGLE_ERROR', err);
          });
      });
    },
    FETCH_CREATE: ({ commit }, payload) => {
      commit('FETCH_CREATE_START');
      return new Promise((resolve, reject) => {
        Vue.prototype
          .$httpActivity({ url: 'subject-activities', data: payload.data, method: 'POST' })
          .then(response => {
            resolve(response);
            commit('START_COUNT_TIME_IN_SECOND');
            commit('FETCH_CREATE_SUCCESS', response);
          })
          .catch(err => {
            reject(err);
            commit('FETCH_CREATE_ERROR', err);
          });
      });
    },
    FETCH_UPDATE: ({ commit }, payload) => {
      commit('FETCH_UPDATE_START');
      commit('STOP_TIMER_IN_SECONDS');
      return new Promise((resolve, reject) => {
        Vue.prototype
          .$httpActivity({
            url: `subject-activities/${payload.id}`,
            data: payload.data,
            method: 'PUT',
          })
          .then(response => {
            resolve(response);
            commit('FETCH_UPDATE_SUCCESS', response);
          })
          .catch(err => {
            reject(err);
            commit('FETCH_UPDATE_ERROR', err);
          });
      });
    },
    FETCH_DELETE: ({ commit }, id) => {
      commit('FETCH_DELETE_START');
      return new Promise((resolve, reject) => {
        Vue.prototype
          .$httpActivity({ url: `subject-activities/${id}`, method: 'DELETE' })
          .then(response => {
            resolve(response);
            commit('FETCH_DELETE_SUCCESS', id);
          })
          .catch(err => {
            reject(err);
            commit('FETCH_DELETE_ERROR', err);
          });
      });
    },
  },
};
